import { FlexComponentMap } from "components/flexFramework/FlexComponents/typings";
import { Logger } from "bernie-logger";
import * as React from "react";
import { ExtendedContextStore } from "typings/flexFramework/FlexDefinitions";
import { ServerSideLogger } from "components/utility/ServerSideLogger";
import { UNSUPPORTED_MODULE } from "config/systemEvents";
import { Component, ComponentProps, ExtendedFTNode } from "src/typings/flexFramework/FlexComponent";
import { Localization } from "bernie-l10n";
import { ItemKeyHelper } from "components/utility/ItemKeyHelper";
import { flexComponentMap } from "components/flexFramework/FlexComponents/flexComponentMap";

/**
 * Base class to describe a component that we are rendering in Blossom.
 * This is an adapter pattern over the components given to use from whatever data source (e.g. LPT Composition).
 *
 * This class knows what it is, and knows how to render itself appropriately regardless of where its data comes from.
 */
export abstract class BlossomComponent {
  public flexTemplateNode: ExtendedFTNode;

  public id: string;

  public fmId: number;

  public fmTitleId?: number;

  public name: string;

  public type: string;

  public modules: Component[];

  public componentMap: FlexComponentMap = flexComponentMap;

  public scene: string | undefined;

  public abstract model: any; // should be typed inside react component

  public abstract getProps: () => any;

  public abstract shouldRender: () => boolean;

  public abstract getView: () => string | undefined;

  public abstract getName: () => string;

  public abstract getBlossomComponents: () => BlossomComponent[];

  protected constructor(module: Component) {
    this.id = module.id;
    this.name = module.name;
    this.fmId = module.fmId;
    this.fmTitleId = module.fmTitleId;
    this.scene = module.scene;
    this.modules = module.modules;
  }

  public render = (key: string, context: ExtendedContextStore, l10n: Localization, logger: Logger) => {
    /* istanbul ignore next */
    if (!this.shouldRender()) {
      return null; //ignoring this for coverage since we are intentionally migrating away from it
    }

    const component = this.getReactComponent();
    if (component === undefined) {
      this.logUnsupportedModule(logger, context);

      return null;
    }

    const props: ComponentProps = {
      ...this.getProps(),
      key,
      context,
      l10n,
      blossomComponent: this,
    };

    return React.createElement(component.codeSplit as any, props);
  };

  public renderChildren = (
    logger: Logger,
    l10n: Localization,
    context: ExtendedContextStore,
    filterFunction?: (component: BlossomComponent) => boolean
  ) => {
    const keyHelper = new ItemKeyHelper("blossomComponent");
    let children = this.getBlossomComponents();
    if (filterFunction) {
      children = children.filter(filterFunction);
    }

    return children
      .map((component) => component.render(component.id || keyHelper.next(), context, l10n, logger))
      .filter((component) => component) as React.ReactElement[];
  };

  /**
   * Gets the react component from the Flex Component Map using its name and view.
   * The convention is: <module name>_<view>
   *
   * e.g. "hotels_travellers-loved"
   */
  public getReactComponent = () => {
    const flexComponentName = this.getName();
    const view = this.getView();

    // If there is a view, let's try to get it from the map
    if (view && this.componentMap.has(`${flexComponentName}_${view}`)) {
      return this.componentMap.get(`${flexComponentName}_${view}`);
    }

    // Default
    return this.componentMap.get(flexComponentName);
  };

  /**
   * Takes the name of a Flex component found in a composition that had no corresponding Flex UI Component, and reports it to the server side logger
   */
  private logUnsupportedModule = (logger: Logger, context: ExtendedContextStore) => {
    const contextualInfo = Object.entries(
      context
        ? {
            pageType: context.searchContext?.pageType,
            deviceId: context.deviceId,
            userAgent: context.userAgent,
            langId: context.langId,
            locale: context.locale,
            siteId: context.site.id,
            requestId: context.traceInfo?.["Trace-ID"],
          }
        : {}
    )
      .map((key, value) => `${key}=\"${value}\"`)
      .toString();

    ServerSideLogger(logger).logEvent(
      UNSUPPORTED_MODULE,
      `moduleName=${this.name} is not currently supported by this view layer application`,
      contextualInfo
    );
  };
}
